import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import xstream from "state-x-stream";
import {
  map,
  distinctUntilChanged,
  filter,
  switchMap,
  catchError,
  of,
  BehaviorSubject,
  takeUntil,
  tap,
  take,
} from "rxjs";
import ZoomArea from "../ZoomArea";
import VerticalGrid from "../VerticalGrid";
import Scrollbar from "../Scrollbar";
import WebGLRenderer from "../WebGLRenderer";
import VerticalGridLabels from "../VerticalGridLabels";
import Brush from "../Brush";
import Header from "../Header";
import FullscreenComponent from "../FullscreenComponent";
import XAxisInfo from "../XAxisInfo";
import Chromosomes from "../Chromosomes";
import Stage from "../../models/Stage";
import ZoomService from "../../services/zoomService/zoom.service";
import Position from "../Position";
import { NATIVE, GREEDY } from "../../constants/options";
import {
  getGenome,
  getChromosome,
} from "../../../../state/selectors/general.selectors";
import BrowserConf from "../../../../models/BrowserConf";
import Genome from "../../../../models/Genome";
import genomes from "../../constants/genomes";
import getApiClient from "../../apiClient";
import styles from "../../RegionBrowser.module.scss";
import Loader from "../../../../components/Loader";
import Sidebar from "../Sidebar";
import isWithinClass from "../../../../common/helpers/isWithinClass";
import eventEmitter from "../../../../components/eventEmitter";

function controller({ props$, destroyed$ }) {
  const browserConf$ = new BehaviorSubject(null);
  const zoomService = new ZoomService({});
  const loading$ = new BehaviorSubject(false);
  const stage = new Stage({ state: props$.value.state });

  function compareKeys(...keys) {
    return (a, b) => {
      for (let i = 0; i < keys.length; i++) {
        if (a[keys[i]] !== b[keys[i]]) return false;
      }
      return true;
    };
  }

  function resolveGenome(genomeName, chrName = "") {
    const chrKey = Genome.toChrKey(chrName);
    const genomeId = genomeName === "hg38" ? "hg38" : "hg19";
    const isMt = chrKey === "mt";
    const mitoChr = isMt ? "mt" : "m";
    const nuclearChrs = genomes.nuclear[genomeId];
    const mitoChrs = genomes.mito[genomeId][mitoChr];
    const chrs = nuclearChrs.concat(mitoChrs);
    const genome = new Genome(genomeId, chrs);
    const chromosome = genome.getChrByName(chrName);
    const generatedForChr = chrName;
    return chromosome
      ? of({ genome, chromosome, generatedForChr })
      : getApiClient()
          .loadChrInfo({ genomeId, chrName })
          .pipe(
            map(({ id, size }) => genome.addChr(id, size, chrName)),
            map((x) => ({
              genome: x,
              generatedForChr,
              chromosome: x.getChrByName(chrName),
            })),
            catchError(() => of(null))
          );
  }

  function handleError(e) {
    console.error(e);
    loading$.next(false);
    return of(null);
  }

  function reset() {
    const { g0: v0, g1: v1 } = browserConf$.value.visRange;
    const { g0: c0, g1: c1 } = browserConf$.value.chromosome.region;
    const { g0: h0, g1: h1 } = zoomService.genome;
    const { value: isWholeGenome } = zoomService.isWholeGenome$;
    const d1 = isWholeGenome ? h0 : c0;
    const d2 = isWholeGenome ? h1 : c1;

    stage.tracks.forEach((x) => x.reset());
    zoomService.setXDomain(d1, d2);
    zoomService.setXVisDomain(v0 - 2, v1 + 2);
    zoomService.jumpTo(v0, v1);
  }

  destroyed$.pipe(take(1)).subscribe(() => stage.destroy());
  destroyed$.pipe(take(1)).subscribe(() => zoomService.dispose());

  props$
    .pipe(
      distinctUntilChanged(compareKeys("genomeId", "chrName")),
      tap(() => loading$.next(true)),
      switchMap(({ genomeId, chrName }) => resolveGenome(genomeId, chrName)),
      tap(() => loading$.next(false)),
      filter(Boolean),
      switchMap((genomeData) => {
        return props$.pipe(
          filter((x) => x?.chrName === genomeData?.generatedForChr),
          map((props) => new BrowserConf({ ...props, ...genomeData }))
        );
      }),
      filter(Boolean),
      tap((x) => browserConf$.next(x)),
      tap((conf) => zoomService.setConf(conf)),
      distinctUntilChanged((a, b) => a?.genome?.key === b?.genome?.key),
      tap(() => stage.tracks.forEach((y) => y.reset())),
      takeUntil(destroyed$),
      catchError(handleError)
    )
    .subscribe();

  props$
    .pipe(
      map(({ trackConfs: x }) => x),
      distinctUntilChanged(),
      tap((trackConfs) => stage.setTracks(trackConfs, zoomService)),
      takeUntil(destroyed$),
      catchError(handleError)
    )
    .subscribe();

  props$
    .pipe(
      map(({ state: x }) => x),
      distinctUntilChanged(),
      tap((state) =>
        stage.setState({ ...stage.state$.value, ...(state || {}) })
      ),
      takeUntil(destroyed$),
      catchError(handleError)
    )
    .subscribe();

  browserConf$
    .pipe(
      filter((x) => x?.selectedRange),
      map((x) => x.selectedRange),
      distinctUntilChanged((a, b) => a.g0 === b.g0 && a.g1 === b.g1),
      tap((x) => stage.setHighlightedArea([x.g0, x.g1])),
      takeUntil(destroyed$),
      catchError(handleError)
    )
    .subscribe();

  return {
    browserConf: browserConf$,
    isLoading: loading$.pipe(distinctUntilChanged()),
    zoomService,
    stage,
    reset,
  };
}

function Pulsar({
  stage,
  zoomService,
  browserConf,
  reset,
  title,
  isLoading,
  genomeId,
  InfoRenderer,
  ...props
}) {
  const pulsarRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [options, setOptions] = useState(stage.preferences$.value);
  const { chromosome, genome, selectedRange } = browserConf || {};
  const { x0, x1 } = selectedRange || {};
  const noChr = !chromosome;
  const isBefore = x0 < chromosome?.region?.x0;
  const isAfter = x1 > chromosome?.region?.x1;
  const hasError = !genome || noChr || isBefore || isAfter;
  const borderCl = isActive ? `tw-border-primary` : `tw-border-transparent`;
  const otherCl = "tw-h-full tw-relative tw-overflow-hidden tw-border-2";

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [isExpanded]);

  useEffect(() => {
    if (!isActive) return () => {};

    function isWithin({ target }) {
      return (
        pulsarRef.current.contains(target) ||
        isWithinClass(target, "default-tooltip-container") ||
        isWithinClass(target, "prevent-lose-focus")
      );
    }

    const sub = eventEmitter.clickTracker$
      .pipe(
        filter(() => Boolean(pulsarRef.current)),
        map((ev) => !isWithin(ev)),
        filter(Boolean)
      )
      .subscribe(() => setIsActive(false));

    return () => sub.unsubscribe();
  }, [isActive, setIsActive]);

  if (!isLoading && hasError) {
    return (
      <div className={styles.container}>
        <div className="tw-border tw-py-8 anim-fade-in tw-text-center">
          Sorry, <span className="tw-font-semibold tw-px-2">{title}</span>
          failed to resolve the genomic coordinates of your query.
        </div>
      </div>
    );
  }

  return (
    <div
      ref={pulsarRef}
      className={`${otherCl} ${styles.container} ${options.themeCl} ${borderCl}`}
      onClick={() => setIsActive(true)}
      onKeyDown={() => setIsActive(true)}
    >
      {isLoading ? (
        <div className="tw-absolute tw-inset-0 tw-bg-gray-400 tw-bg-opacity-20 tw-z-40 tw-flex tw-justify-center tw-items-center">
          <Loader />
        </div>
      ) : null}

      <FullscreenComponent
        isFullscreen={isExpanded}
        isNative={options.fullScreenMode === NATIVE}
        className={options.themeCl || "-"}
        exitFullscreen={() => setIsExpanded(false)}
      >
        <div className="tw-flex bg-1 tw-w-full tw-flex-col tw-h-full graph-container tw-select-none tw-transition-colors transition-duration-200">
          <div tabIndex={0} />
          <Header
            stage={stage}
            genomeId={genomeId}
            zoomService={zoomService}
            themeCl={options.themeCl}
            setOptions={setOptions}
            toggleExpanded={setIsExpanded}
            isExpanded={isExpanded}
          >
            {title}
          </Header>

          <div className="tw-flex-grow tw-flex tw-h-full transition-duration-200 tw-border-t border-1">
            <div className="tw-flex-grow tw-relative">
              <ZoomArea
                isActive={isActive || isExpanded}
                isGreedy={isExpanded || options.gestureControl === GREEDY}
                zoomService={zoomService}
                zoomTrackingFactor={Number(options.zoomTrackingFactor) || 1}
              >
                <div className="tw-flex tw-flex-col tw-absolute tw-inset-0 tw-overflow-hidden">
                  <Position zoomService={zoomService} stage={stage} />
                  <div className="tw-relative border-1 tw-h-10">
                    <div className="tw-absolute tw-inset-0 tw-overflow-hidden">
                      <Brush zoomService={zoomService} />
                    </div>
                    <div className="tw-relative tw-pointer-events-none tw-py-2 tw-h-10">
                      <Chromosomes zoomService={zoomService} height={14} />
                    </div>
                    <XAxisInfo zoomService={zoomService} />
                  </div>
                  <div className="tw-relative tw-h-5 tw-border-t border-1" />
                  <div className="tw-relative tw-h-6 tw-border-t border-1">
                    <VerticalGridLabels zoomService={zoomService} />
                  </div>
                  <div className="tw-flex-grow tw-relative tw-overflow-hidden">
                    <VerticalGrid zoomService={zoomService} />
                    <WebGLRenderer stage={stage} zoomService={zoomService} />
                    <Scrollbar zoomService={zoomService} />
                  </div>
                </div>
              </ZoomArea>
            </div>

            <Sidebar
              {...props}
              stage={stage}
              zoomService={zoomService}
              reset={reset}
              toggleExpanded={setIsExpanded}
              isExpanded={isExpanded}
              themeCl={options.themeCl}
              setOptions={setOptions}
              InfoRenderer={InfoRenderer}
            />
          </div>
        </div>
      </FullscreenComponent>
    </div>
  );
}

Pulsar.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  zoomService: PropTypes.instanceOf(ZoomService).isRequired,
  stage: PropTypes.instanceOf(Stage).isRequired,
  browserConf: PropTypes.instanceOf(BrowserConf),
  reset: PropTypes.func.isRequired,
  genomeId: PropTypes.string.isRequired,
  InfoRenderer: PropTypes.func,
};

Pulsar.defaultProps = {
  browserConf: null,
  InfoRenderer: null,
};

const mapStateToProps = (state) => ({
  chrName: getChromosome(state),
  genomeId: getGenome(state).name,
});

const Comp = xstream(controller)(Pulsar);
const Connected = connect(mapStateToProps)(Comp);
export default Connected;
